/**
 * Event Types
 * Simplified event types for chat service communication
 * 
 * Supports:
 * - Lifecycle events (run start/finish/error, step start/finish)
 * - Text message events (streaming text content)
 */

// ============================================================================
// Lifecycle Events
// Monitor the progression of agent runs
// ============================================================================

export const LIFECYCLE_EVENTS = {
    /** First event when an agent begins processing a request */
    RUN_STARTED: 'RUN_STARTED',
    /** Signals successful completion of an agent run */
    RUN_FINISHED: 'RUN_FINISHED',
    /** Signals an error occurred during the run */
    RUN_ERROR: 'RUN_ERROR',
    /** Signals the start of a discrete subtask within a run */
    STEP_STARTED: 'STEP_STARTED',
    /** Signals completion of a subtask */
    STEP_FINISHED: 'STEP_FINISHED'
};

// ============================================================================
// Text Message Events
// Handle streaming textual content (Start → Content* → End pattern)
// ============================================================================

export const TEXT_MESSAGE_EVENTS = {
    /** Initializes a new message with messageId and role */
    TEXT_MESSAGE_START: 'TEXT_MESSAGE_START',
    /** Delivers incremental text chunks (delta) */
    TEXT_MESSAGE_CONTENT: 'TEXT_MESSAGE_CONTENT',
    /** Marks message as complete */
    TEXT_MESSAGE_END: 'TEXT_MESSAGE_END',
    /** Convenience event that auto-expands to Start→Content→End (first chunk must include messageId) */
    TEXT_MESSAGE_CHUNK: 'TEXT_MESSAGE_CHUNK'
};

// ============================================================================
// Combined Event Types
// ============================================================================

export const EVENT_TYPES = {
    ...LIFECYCLE_EVENTS,
    ...TEXT_MESSAGE_EVENTS
};

// ============================================================================
// Emitted Event Names (Internal Frontend Events)
// These are the event names used in the event emitter (useChatService.on/emit)
// ============================================================================

export const EMITTED_EVENTS = {
    // Lifecycle events (camelCase versions for internal use)
    RUN_STARTED: 'runStarted',
    RUN_FINISHED: 'runFinished',
    RUN_ERROR: 'runError',
    STEP_STARTED: 'stepStarted',
    STEP_FINISHED: 'stepFinished',
    
    // Text message events
    TEXT_MESSAGE_START: 'textMessageStart',
    TEXT_MESSAGE_CONTENT: 'textMessageContent',
    TEXT_MESSAGE_END: 'textMessageEnd',
    
    // Connection events
    CONNECTED: 'connected',
    DISCONNECTED: 'disconnected',
    FALLBACK_TO_HTTP: 'fallbackToHTTP',
    
    // Error events
    PARSE_ERROR: 'parseError',
    
    // Raw event passthrough
    EVENT: 'event'
};

// ============================================================================
// Event Category Lookup
// ============================================================================

export const EVENT_CATEGORIES = {
    LIFECYCLE: 'lifecycle',
    TEXT_MESSAGE: 'text_message'
};

/**
 * Get the category for a given event type
 * @param {string} eventType - The event type
 * @returns {string|null} - The event category or null if unknown
 */
export const getEventCategory = (eventType) => {
    if (Object.values(LIFECYCLE_EVENTS).includes(eventType)) {
        return EVENT_CATEGORIES.LIFECYCLE;
    }
    if (Object.values(TEXT_MESSAGE_EVENTS).includes(eventType)) {
        return EVENT_CATEGORIES.TEXT_MESSAGE;
    }
    return null;
};

export default EVENT_TYPES;
